import type { ImageViewResponse } from '@api/types/chat'

/**
 * Opening a view-limited chat image.
 *
 * The server never gives out an address for one: opening it spends a view (the sender's own preview
 * is free) and returns a one-time token, and the bytes come from GET /api/Chat/images/{token} with
 * the auth header. They are shown from an object URL that lives only while the viewer is open, so
 * nothing reusable is left behind in the page, the history or the HTTP cache (the response is
 * no-store).
 */
export interface OpenedImage {
  /** blob: URL (view-limited) or the ordinary image's URL. */
  url: string
  remaining: number | null
  /** Frees the in-memory copy. Call when the viewer closes. */
  release: () => void
}

/** The view was spent (or the token issued) but the bytes never arrived. Never retried with another view. */
export class ProtectedImageLoadError extends Error {
  constructor(public readonly remaining: number | null) {
    super("Couldn't load the photo")
    this.name = 'ProtectedImageLoadError'
  }
}

export interface ProtectedImageDeps {
  viewImage: (messageId: string) => Promise<ImageViewResponse>
  fetchImageByToken: (token: string) => Promise<Blob>
  createObjectUrl?: (blob: Blob) => string
  revokeObjectUrl?: (url: string) => void
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status
}

/**
 * The token endpoint lets a failed delivery be retried with the same token inside its minute, so a
 * dropped connection does not cost a view. A 404/410 means the token is spent or gone: no retry.
 */
async function fetchWithOneRetry(deps: ProtectedImageDeps, token: string): Promise<Blob> {
  try {
    return await deps.fetchImageByToken(token)
  } catch (error) {
    const status = statusOf(error)
    if (status === 404 || status === 410 || status === 401 || status === 403) throw error
    return deps.fetchImageByToken(token)
  }
}

export async function openChatImage(messageId: string, deps: ProtectedImageDeps): Promise<OpenedImage> {
  const createUrl = deps.createObjectUrl ?? ((blob: Blob) => URL.createObjectURL(blob))
  const revokeUrl = deps.revokeObjectUrl ?? ((url: string) => URL.revokeObjectURL(url))

  // Throws (403) when the viewer is out of views: the caller locks the image.
  const view = await deps.viewImage(messageId)

  if (!view.viewToken) {
    if (view.imageUrl) return { url: view.imageUrl, remaining: view.viewsRemaining, release: () => {} }
    throw new ProtectedImageLoadError(view.viewsRemaining)
  }

  let blob: Blob
  try {
    blob = await fetchWithOneRetry(deps, view.viewToken)
  } catch {
    throw new ProtectedImageLoadError(view.viewsRemaining)
  }

  const url = createUrl(blob)
  let released = false
  return {
    url,
    remaining: view.viewsRemaining,
    release: () => {
      if (released) return
      released = true
      revokeUrl(url)
    },
  }
}
